<template>
  <article class="portfolio-card" :class="{ 'is-new': project.new }">
    <Link class="portfolio-card-link" :href="route('portfolios.show', project.slug)">
      <figure ref="img" class="portfolio-card-media">
        <img :src="project.image" :alt="project.domain" loading="lazy" />

        <!-- The overlay is the card's own affordance, not a second link. It used
             to be an anchor to the client's live site sitting inside a card that
             also linked out, which gave one destination two names. The card opens
             the case page now, and that page is where the work is described. -->
        <span class="portfolio-card-cta">
          {{ t('View project') }}
          <font-awesome-icon icon="arrow-right" aria-hidden="true" />
        </span>

        <!-- A real element rather than the `content: 'New'` pseudo-element it
             used to be, which put a word on the page that no dictionary could
             reach and so stayed English in all three languages. -->
        <span v-if="project.new" class="portfolio-card-new">{{ t('New') }}</span>
      </figure>

      <span class="portfolio-card-domain">{{ project.domain }}</span>

      <!-- Tags as the words the catalog prints, on one line. Plain text rather
           than chips: this is how the portfolio has always tagged a project, and
           the mobile page's pills are that section's own treatment of the same
           six words, not a second house style for this card. -->
      <p class="portfolio-card-badges">{{ badgesText }}</p>
    </Link>
  </article>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The old `.project`, lifted out of the home section so the band and the listing
   render one card. The section carried this markup twelve times over, which made
   every change to the card twelve changes to near copies of the same block. */
.portfolio-card {
  margin-bottom: 3rem;
  position: relative;
  text-align: center;
}

/* The card is one link, so it inherits the type rather than taking the browser's
   link blue, and the underline stays off: the overlay and the lift on the
   screenshot are the affordance. */
.portfolio-card-link {
  color: inherit;
  display: block;
  text-decoration: none;

  &:focus-visible {
    outline: 3px solid $ring-focus;
    outline-offset: 3px;
  }
}

.portfolio-card-media {
  margin-bottom: 1rem;
  position: relative;

  img {
    border-radius: $r-sm;
    box-shadow: $shadow-sm;
    display: block;
    height: 250px;
    object-fit: cover;
    object-position: top;
    overflow: hidden;
    width: 100%;

    /* Below md the card is the width of the page, where a 250px box is close to
       square and crops the screenshot hard. */
    @media (max-width: 767.98px) {
      height: 190px;
    }
  }
}

/* Revealed by the pointer, and shown outright below lg where there is no hover
   to reveal it with. Opacity rather than display, so the label stays in the
   accessible tree and in the flow; it is not itself focusable, because the card
   is the single link and tabbing into the card reveals it the way hovering
   does. */
.portfolio-card-cta {
  align-items: center;
  background: $ground-dark;
  border-radius: $r-sm;
  color: $on-dark-strong;
  display: inline-flex;
  font-weight: 500;
  gap: 0.45rem;
  left: 50%;
  opacity: 0;
  padding: 0.5rem 1rem;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translate(-50%, calc(-50% + 0.5rem));
  transition: background-color $dur $ease, opacity $dur $ease, transform $dur $ease;

  /* Sized to its label rather than to whatever the offset left it. An
     absolutely positioned box with `left: 50%` and no width is measured
     against the half of the card to its right, so on a three-up row the label
     ran out of room and broke onto two lines. The overlay is centered by the
     translate, so it is free to be as wide as its own content. */
  white-space: nowrap;
  width: max-content;

  svg {
    font-size: 0.72rem;
  }

  @media (max-width: 991.98px) {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
}

.portfolio-card-link:hover .portfolio-card-cta,
.portfolio-card-link:focus-visible .portfolio-card-cta {
  background: $secondary;
  opacity: 1;
  transform: translate(-50%, -50%);
}

/* The tab over the top-left corner of the screenshot, on the three recent
   builds. */
.portfolio-card-new {
  background-color: $secondary;
  border-radius: $r-xs;
  box-shadow: 0 0.25rem 0.5rem rgba($secondary, .5);
  color: $on-dark-strong;
  font-size: 0.85rem;
  font-weight: 500;
  left: -0.5rem;
  padding: 0 0.5rem;
  position: absolute;
  top: 0.5rem;
  z-index: 5;
}

/* A span, not a heading. This is card metadata: a hostname identifies the card
   rather than opening a section, and thirteen of them in the outline made the
   page's own structure harder to read, not easier. */
.portfolio-card-domain {
  color: $text-heading;
  display: inline-block;
  font-family: $headings-font-family;
  font-size: 1.25rem;
  font-weight: $headings-font-weight;
}

.portfolio-card-badges {
  margin-bottom: 0;
}
</style>

<script>
import { Link } from '@inertiajs/inertia-vue3';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';
import VanillaTilt from 'vanilla-tilt';
import { portfolioMessages } from '@/i18n/portfolios';

FontAwesomeLibrary.add(faArrowRight);

export default {
  components: {
    Link
  },

  props: {
    project: {
      type: Object,
      required: true
    }
  },

  computed: {
    /* The tags arrive as the English words the catalog prints and are
       translated here, so the six words are declared once, in the shared
       dictionary, rather than once per project. */
    badgesText() {
      return (this.project.badges || []).map((badge) => this.t(badge)).join(', ');
    }
  },

  mounted() {
    /* The tilt is this card's own gesture, so it is set up here rather than by
       whichever page happens to render the grid. */
    var image = this.$refs.img ? this.$refs.img.querySelector('img') : null;

    if (image) {
      VanillaTilt.init(image, {
        'max-glare': 0.25,
        glare: true,
        max: 5,
        scale: 1
      });
    }
  },

  beforeUnmount() {
    /* VanillaTilt listens on the window as well as on the element, so a card
       that leaves on an Inertia navigation has to be taken down by hand. The old
       section set the tilt up on every mount and never removed one, so each
       visit to a page that showed the band left its listeners behind. */
    var image = this.$refs.img ? this.$refs.img.querySelector('img') : null;

    if (image && image.vanillaTilt) {
      image.vanillaTilt.destroy();
    }
  },

  setup() {
    /* The card's own labels come from the portfolio dictionary, imported rather
       than declared here: the tags, "View project" and the "New" tab are shared
       with the listing and the case page, and the same words must not be
       translated twice. */
    const { t } = useI18n({ messages: portfolioMessages });

    return { t };
  }
};
</script>
